import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import { CdnService } from '../../../../_services/_cdn/cdn.service';
import { ShgProfileService } from '../../../../_services/_profile/shg-profile.service';
import { CommonService } from '../../../../_services/_common/common.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'lokos-resolution-copy',
    templateUrl: './resolution-copy.component.html',
    styleUrls: ['./resolution-copy.component.scss'],
    standalone: false,
})
export class ResolutionCopyComponent implements OnInit {
    guidelines = [
        this.translate.instant('shgGuidelinesText1'),
        this.translate.instant('shgGuidelinesText2'),
        this.translate.instant('shgGuidelinesText3'),
        this.translate.instant('shgGuidelinesText4'),
        this.translate.instant('shgGuidelinesText5'),
    ];

    // Date and Location Information
    todayData: any = new Date();
    shgId: any;
    shgName: any;
    shgCode: any;
    stateId: any;
    districtId: any;
    blockId: any;
    panchayatId: any;
    villageId: any;
    stateName: any;
    distName: any;
    blockName: any;
    panchayatName: any;
    villageName: any;
    formationDate: any;

    // Error and Lookup Data
    errorMessage: any;
    lookupDataList: any = [];

    // SHG Member Data
    shgMembersLists: any = [];
    totalRecord: any = 0;
    splitShgMembersListsData: any = [];

    // Configuration
    imageUrl: any = environment.reportsApiBaseUrl;
    totalTempShgCodeDigits = 8;
    showRefreshBtn = false;
    regShgId: any;

    // SHG Details
    contId: any;
    contCode: any;
    contName: any;
    contGuid: any;
    shgGuid: any;
    regType: any;
    shgKeyword: any;
    cboUuid: any;

    // Designation Details
    shgDesignationDetails: any[] = [];
    shgDesigPresident: any[] = [];
    shgDesigSecretary: any[] = [];
    shgDesigTreasurer: any[] = [];

    // Bank Details
    BankBranchMasterList: any = [];
    ShgBankList: any[] = [];
    ShgMobileList: any[] = [];
    bankIfsc: any;
    BankBranchName: any;

    // Profile Details
    ShgProfileDetail: any;
    isSmallScreen = false;
    headerLeader = '';
    breadcrumbs: any[] = [];

    constructor(
        private route: ActivatedRoute,
        private cdnService: CdnService,
        private shgProfileService: ShgProfileService,
        private commonService: CommonService,
        private translate: TranslateService
    ) { }

    ngOnInit(): void {
        if (window.location.href.indexOf('refresh-btn') > -1) {
            this.showRefreshBtn = true;
        } else {
            this.showRefreshBtn = false;
        }
        this.showRefreshBtn = true;

        this.route.queryParams.subscribe((params) => {
            // OLD portal encoded all params as a single base64 `queryParams` string.
            // NEW portal passes each param directly in the query string — decode
            // accordingly so the component works with both routing strategies.
            const rawParams = this.decodeQueryParams(params);
            this.initializeFromParams(rawParams);
            this.getLookupData();

            if (this.regType === 'SHG') {
                this.headerLeader = 'shg';
                this.breadcrumbs = [
                    { label: 'shgProfile', link: '/main/profile/shg-profile' },
                ];
            }

            this.breadcrumbs.push({
                label: 'Registration Copy',
                link: '',
            });
        });
    }

    /**
     * Supports both portals' param encoding strategies:
     *  - New portal: flat query params  (contId=..., shg_name=..., ...)
     *  - Old portal: single base64 blob (?queryParams=<base64>)
     */
    private decodeQueryParams(params: any): Record<string, string> {
        if (params['queryParams']) {
            // Old-portal encoding: atob + URLSearchParams
            try {
                const decoded = atob(params['queryParams']);
                const urlParams = new URLSearchParams(decoded);
                const result: Record<string, string> = {};
                urlParams.forEach((value, key) => (result[key] = value));
                return result;
            } catch {
                // Fallback — return raw params as-is
                return params;
            }
        }
        // New portal: plain params object
        return params;
    }

    // Initialize component from (decoded) route parameters
    private initializeFromParams(params: Record<string, string>): void {
        this.shgId = params['contId'];
        this.shgName = params['shg_name'];
        this.shgCode = params['cont_code'];
        this.contId = params['contId'];
        this.contName = params['cont_name'];
        this.contCode = params['cont_code'];
        // Old portal used 'uuid'; new portal uses 'cont_uuid' — accept both
        this.cboUuid = params['cont_uuid'] ?? params['uuid'];

        this.stateId = params['stateId'];
        this.districtId = params['districtId'];
        this.stateName = params['stateName'];
        this.distName = params['distName'];
        this.blockId = params['blockId'];
        this.panchayatId = params['panchayatId'];
        this.villageId = params['villageId'];
        this.blockName = params['blockName'];
        this.panchayatName = params['panchayatName'];
        this.villageName = params['villageName'];
        this.formationDate = params['formation_date'];
        this.shgGuid = params['contGuid'];
        this.contGuid = params['contGuid'];
        this.regType = params['regType'];

        if (this.shgCode === '0') {
            this.getTempShgCode();
        }

        if (this.regType === 'Member') {
            this.shgKeyword = 'SHG';
        }
    }

    // Generate a temporary SHG code when no permanent code exists
    private getTempShgCode(): void {
        let tempShgCode = `T-${this.stateId}`;
        this.totalTempShgCodeDigits =
            this.totalTempShgCodeDigits - this.shgId.toString().length;
        for (let i = 0; i < this.totalTempShgCodeDigits; i++) {
            tempShgCode += '0';
        }
        tempShgCode += this.shgId.toString();
        this.shgCode = tempShgCode;
    }

    // Refresh cache and reload all data (bound to Refresh Data button)
    forceCacheUpdate(): void {
        this.loadSghMembers();
        this.LoadShgDesignationList();
        this.LoadShgBankList();
    }

    // ─── Data Loading ─────────────────────────────────────────────────────────

    /**
     * Fetch lookup master data via the new portal's CdnService, then kick off
     * all dependent data loads that need the lookup list ready first.
     *
     * Old portal loaded from IndexedStorageService.get('allMaster') synchronously
     * before calling the other loaders. New portal fetches asynchronously via CDN
     * and chains the calls in the subscription callback.
     */
    getLookupData(): void {
        this.cdnService.getLokosMasterData().subscribe({
            next: (response: any) => {
                this.lookupDataList =
                    response.find((o: any) => o.name === 'lookUpMasterList')
                        ?.json || [];

                // Dependent loads — mirror the old portal's ngOnInit sequencing
                this.loadSghMembers();
                this.LoadShgDesignationList();
                this.LoadShgBankList();
                this.LoadShgBasicDetails();
            },
            error: (error: any) => {
                console.error('Error fetching lookup data:', error);
            },
        });
    }

    // Load active SHG members, enrich with lookup values, chunk for Annexure pages
    loadSghMembers(): void {
        this.shgProfileService.GetShgMemberList(this.cboUuid).subscribe(
            (response: any) => {
                if (response) {
                    const shgMembersListsData = response;
                    this.totalRecord = shgMembersListsData?.length;

                    shgMembersListsData?.forEach((value: any) => {
                        this.enrichMemberData(value);
                    });

                    setTimeout(() => {
                        this.shgMembersLists = shgMembersListsData.filter(
                            (element: any) => element.status === 1
                        );
                        // Old portal used lodash _.orderBy; new portal uses native sort
                        this.shgMembersLists.sort((a: any, b: any) => {
                            if (a.status !== b.status) {
                                return a.status - b.status;
                            }
                            return a.designation - b.designation;
                        });
                        this.splitShgMembersListsData = this.chunkArray(
                            this.shgMembersLists,
                            8
                        );
                        console.log(this.splitShgMembersListsData);
                    }, 200);
                }
            },
            (error: any) => {
                this.errorMessage = error;
                console.error('Error loading SHG members:', error);
            }
        );
    }

    // Enrich a single member record with designation label, social category, and age
    private enrichMemberData(member: any): void {
        this.lookupDataList.forEach((element: any) => {
            if (
                element.key1 === 1 &&
                element.lookup_code === member.designation &&
                element.language_id === 'en'
            ) {
                member['mem_designation'] = element.key_value;
            }

            if (
                element.key1 === 2 &&
                element.key_code === 'socialCategory' &&
                element.lookup_code === member.social_category &&
                element.language_id === 'en'
            ) {
                member['social_class'] = element.key_value;
            }
        });

        member['age'] = this.ageCalculator(member.member_date_of_birth);
    }

    // Calculate age from a Unix-timestamp date of birth
    ageCalculator(dateOfBirth: any): number {
        const timeDiff = Math.abs(Date.now() - dateOfBirth * 1000);
        return Math.floor(timeDiff / (1000 * 3600 * 24) / 365);
    }

    // Returns the display index (1-based) of a member within shgMembersLists
    findIndex(memCode: any): number {
        return this.shgMembersLists
            .map((e: any) => e.member_code)
            .indexOf(memCode);
    }

    // Split a flat array into pages of `size` items each (used for Annexure pagination)
    private chunkArray(array: any[], size: number): any[][] {
        const chunks = [];
        for (let i = 0; i < array.length; i += size) {
            chunks.push(array.slice(i, i + size));
        }
        return chunks;
    }

    // Load designation list and split into President / Secretary / Treasurer
    LoadShgDesignationList(): void {
        this.shgProfileService.GetShgDesignationList(this.cboUuid).subscribe(
            (response: any) => {
                if (response) {
                    // De-duplicate by member_name (mirrors old portal logic)
                    const shgDesignationDetailsUnique = response.filter(
                        (obj: any, index: any) =>
                            index ===
                            response.findIndex(
                                (o: any) => obj.member_name === o.member_name
                            )
                    );

                    // Old portal used lodash _.orderBy; new portal uses native sort
                    this.shgDesignationDetails =
                        shgDesignationDetailsUnique.sort((a: any, b: any) => {
                            if (a.status !== b.status) {
                                return a.status - b.status;
                            }
                            return a.designation - b.designation;
                        });

                    // Old portal ran dates through DateFunctionService.dateFunction();
                    // new portal's formatDate is a passthrough — extend if needed.
                    this.shgDesignationDetails.forEach((values: any) => {
                        if (values.from_date) {
                            values.from_date = this.formatDate(
                                values.from_date
                            );
                        }
                        if (values.to_date) {
                            values.to_date = this.formatDate(values.to_date);
                        }
                    });

                    this.shgDesigPresident = this.shgDesignationDetails.filter(
                        (data: any) => !data?.to_date && data?.designation === 1
                    );
                    this.shgDesigSecretary = this.shgDesignationDetails.filter(
                        (data: any) => !data?.to_date && data?.designation === 2
                    );
                    this.shgDesigTreasurer = this.shgDesignationDetails.filter(
                        (data: any) => !data?.to_date && data?.designation === 3
                    );
                }
            },
            (error: any) => {
                console.error('Error loading SHG designation list:', error);
            }
        );
    }

    /**
     * Date formatting utility.
     * Old portal delegated to DateFunctionService.dateFunction().
     * Replace the passthrough below with the new portal's equivalent pipe/service
     * once the target format is confirmed.
     */
    private formatDate(dateValue: any): any {
        if (!dateValue) return null;
        return dateValue;
    }

    // Load SHG profile details (meeting frequency, etc.)
    LoadShgBasicDetails(): void {
        this.shgProfileService.GetShgBasicDetails(this.cboUuid).subscribe(
            (response: any) => {
                if (response) {
                    this.ShgProfileDetail = response;
                }
            },
            (error: any) => {
                console.error('Error loading SHG basic details:', error);
            }
        );
    }

    // Resolve a lookup code to its display label
    getValueForLookup(keyCode: number, lookUp: any): string {
        let retValue = '';
        this.lookupDataList.forEach((element: any) => {
            if (
                element.key1 === keyCode &&
                element.lookup_code === lookUp &&
                element.language_id === 'en'
            ) {
                retValue = element.key_value;
            }
        });
        return retValue;
    }

    // Load SHG bank accounts and enrich with master bank/branch data
    LoadShgBankList(): void {
        this.shgProfileService.GetShgBankList(this.cboUuid).subscribe(
            (response: any) => {
                if (response.responseCode === '200') {
                    this.ShgBankList = response.responseData;
                    this.loadBankDetails();
                }
            },
            (error: any) => {
                console.error('Error loading SHG bank list:', error);
            }
        );
    }

    /**
     * Merge bank master data into ShgBankList.
     * Old portal read bankMasterList and bankBranchMasterList from IndexedStorageService.
     * New portal fetches the same data from CdnService.getLokosMasterData().
     */
    private loadBankDetails(): void {
        this.cdnService.getLokosMasterData().subscribe({
            next: (masterData: any) => {
                const bankDetails =
                    masterData.find((o: any) => o.name === 'bankMasterList')
                        ?.json || [];
                const bankBranchData =
                    masterData.find(
                        (o: any) => o.name === 'bankBranchMasterList'
                    )?.json || [];
                this.BankBranchMasterList = bankBranchData;

                // Attach bank_name to each bank account entry
                this.ShgBankList?.forEach((value: any) => {
                    this.bankIfsc = value?.ifsc_code;
                    bankDetails?.forEach((element: any) => {
                        if (value.bank_id === element.bank_id) {
                            value['bank_name'] = element.bank_name;
                        }
                    });
                });

                // Promote the default bank account to position 0
                this.ShgBankList?.forEach((defaultValue: any, key: any) => {
                    if (defaultValue.is_default === 1) {
                        this.ShgBankList.splice(key, 1);
                        this.ShgBankList.unshift(defaultValue);
                    }
                });

                // Attach branch address and name
                this.ShgBankList?.forEach((val: any) => {
                    this.BankBranchMasterList?.forEach((value: any) => {
                        if (value.bank_branch_id === val.bank_branch_id) {
                            val['bank_branch_address'] =
                                value.bank_branch_address;
                            val['bank_branch_name'] = value.bank_branch_name;
                        }
                    });
                });
            },
            error: (error: any) => {
                console.error('Error loading bank details:', error);
            },
        });
    }

    // Trigger browser print dialog
    printPage(): void {
        const content: any = document.getElementById('contentToConvert222');
        if (content) {
            content.style.height = '95.5rem';
        }
        window.print();
        window.location.reload();
    }
    /**
     * Prints only the content inside #printAbleArea by:
     * 1. Cloning the target element's inner HTML
     * 2. Opening an isolated popup window with embedded styles
     * 3. Triggering print and closing the popup afterward
     */
    printDocument(): void {
        const printArea = document.getElementById('printAbleArea');
        if (!printArea) {
            console.warn('printAbleArea element not found');
            return;
        }

        const content = printArea.innerHTML;
        const printWindow = window.open('', '_blank', 'width=1200,height=900');
        if (!printWindow) {
            console.warn(
                'Popup blocked — allow popups for this site to print.'
            );
            return;
        }

        printWindow.document.write(`
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8" />
                <title>DAY-NRLM Resolution Copy</title>
                <style>
                    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

                    body {
                        font-family: 'Segoe UI', 'Trebuchet MS', sans-serif;
                        font-size: 13.5px;
                        color: #212121;
                        background: #fff;
                        padding: 0;
                    }

                    /* Hide the print button inside the popup */
                    .print-bar { display: none !important; }

                    /* Wrapper — reset to plain block for print */
                    .resolution-wrapper {
                        display: block;
                        padding: 24px 32px;
                        background: #fff;
                    }

                    .resolution-card {
                        width: 100%;
                        max-width: 100%;
                        background: #fff;
                        border: none;
                        box-shadow: none;
                        border-radius: 0;
                        padding: 0;
                    }

                    /* Header */
                    .resolution-header {
                        display: flex; flex-direction: row; align-items: center;
                        justify-content: space-between; gap: 16px;
                        padding-bottom: 16px; border-bottom: 2px solid #c8860a;
                        margin-bottom: 16px;
                    }
                    .header-logo { flex: 0 0 64px; display: flex; align-items: center; justify-content: center; }
                    .logo-img { width: 56px; height: 56px; object-fit: contain; }
                    .header-center { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px; text-align: center; }
                    .header-title { font-family: Georgia, serif; font-size: 22px; font-weight: 700; color: #1a3a5c; letter-spacing: 1px; }
                    .header-subtitle { font-size: 12.5px; font-weight: 600; color: #c8860a; line-height: 1.5; }

                    /* Date */
                    .date-row { display: flex; gap: 6px; font-size: 13px; margin-bottom: 12px; }
                    .date-label { font-weight: 600; color: #1a3a5c; }

                    /* Intro */
                    .intro-para { font-size: 13px; line-height: 1.7; margin-bottom: 10px; }
                    .underline-field { border-bottom: 1.5px dotted #546e7a; padding-bottom: 1px; }
                    .bold-field { font-weight: 700; color: #1a3a5c; }

                    /* SHG Fields */
                    .shg-field-row { display: flex; flex-direction: row; gap: 10px; margin-bottom: 6px; }
                    .field-box {
                        flex: 1; display: flex; flex-direction: row; align-items: center;
                        gap: 8px; border: 1px solid #b0bec5; border-radius: 4px;
                        padding: 7px 12px; background: #fafbfc; font-size: 13px; min-height: 36px;
                    }
                    .full-width { flex: 1 1 100%; }
                    .field-label { font-weight: 600; color: #546e7a; white-space: nowrap; font-size: 12px; }
                    .field-value { font-weight: 600; color: #1a3a5c; font-size: 13px; }

                    /* Bullets */
                    .resolution-bullets { margin: 14px 0 14px 18px; display: flex; flex-direction: column; gap: 5px; }
                    .resolution-bullets li { font-size: 13px; line-height: 1.7; list-style-type: disc; }

                    /* Guidelines */
                    .guidelines-section { margin-top: 10px; margin-bottom: 14px; }
                    .guidelines-title { font-size: 13.5px; font-weight: 700; color: #1a3a5c; margin-bottom: 7px; }
                    .guidelines-list { margin: 0 0 0 18px; display: flex; flex-direction: column; gap: 6px; }
                    .guidelines-list li { font-size: 12.5px; line-height: 1.7; list-style-type: decimal; }

                    /* Resolution statement */
                    .resolution-statement { font-size: 13px; font-style: italic; color: #546e7a; margin-bottom: 20px; }

                    /* Signatories */
                    .signatories-row {
                        display: flex; flex-direction: row; gap: 24px;
                        padding-top: 16px; border-top: 1px solid #b0bec5; margin-bottom: 16px;
                    }
                    .signatory-block { flex: 1; display: flex; flex-direction: column; gap: 4px; }
                    .otp-label { font-size: 12px; font-weight: 600; color: #546e7a; margin-bottom: 4px; }
                    .signature-tick { width: 55px;    height: 55px;    margin-bottom: 6px;    margin-top: 18px;    position: absolute; }
                    .signature-tick svg { width: 100%; height: 100%; }
                    .signatory-name { font-size: 12.5px; color: #212121; }
                    .signatory-name strong { color: #1a3a5c; }
                    .signatory-designation { font-size: 12px; color: #1a3a5c; }

                    /* Annexure */
                    .annexure-section { margin-top: 50px; padding-top: 20px; border-top: 2px solid #c8860a; page-break-before: auto; }
                    .annexure-title { font-size: 14px; font-weight: 700; color: #1a3a5c; margin-bottom: 14px; }
                    .member-table { display: flex; flex-direction: column; border: 1px solid #b0bec5; border-radius: 6px; overflow: hidden; }
                    .member-row { display: flex; flex-direction: row; align-items: stretch; border-bottom: 1px solid #b0bec5; }
                    .member-row:last-child { border-bottom: none; }
                    .member-row--header { background: #eef2f7; font-weight: 600; font-size: 12px; color: #1a3a5c; }
                    .member-row--even { background: #f9fafc; }
                    .member-cell {
                        display: flex; align-items: center; padding: 9px 12px;
                        font-size: 12.5px; color: #212121;
                        border-right: 1px solid #b0bec5; line-height: 1.4;
                    }
                    .member-cell:last-child { border-right: none; }
                    .member-cell--sno       { flex: 0 0 52px;  justify-content: center; }
                    .member-cell--name      { flex: 2 1 0; }
                    .member-cell--relative  { flex: 1 1 0; }
                    .member-cell--class     { flex: 0 0 80px;  justify-content: center; }
                    .member-cell--age       { flex: 0 0 52px;  justify-content: center; }
                    .member-cell--designation { flex: 1 1 0; }
                    .member-cell--signature { flex: 0 0 100px; min-height: 40px; }

                    /* Footer */
                    .footer-note { font-size: 11px; color: #90a4ae; font-style: italic; margin-top: 12px; }

                    @media print {
                        body { padding: 0; }
                        .resolution-wrapper { padding: 12px 20px; }
                        .annexure-section { page-break-before: auto; }
                    }
                </style>
            </head>
            <body>
                <div class="resolution-wrapper">
                    <div class="resolution-card">
                        ${content}
                    </div>
                </div>
                <script>
                    window.onload = function () {
                        window.print();
                        window.onafterprint = function () { window.close(); };
                    };
                <\/script>
            </body>
            </html>
        `);
        printWindow.document.close();
    }
}
